// Vista "Mi Equipo" — solo para Director/Subdirector de Área.
// Muestra: lista del equipo con estado hoy + calendario mensual del equipo + alerta de cobertura.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Avatar, Chip, Tooltip, IconButton, Tabs, Tab,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, CircularProgress, Alert,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { nodeApiPath } from '../../utils/paths';
import { triggerSessionExpired } from '../../api/endpoints';

const BR = '4px';
const PRIMARY = '#0277bd';
const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

async function jf(path) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No token'); }
    const res = await fetch(nodeApiPath(path), { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

const colorEstadoHoy = (estado) => ({
    Normal: { bg: '#e8f5e9', color: '#2e7d32', label: 'Presente' },
    Tardanza: { bg: '#fff3e0', color: '#e65100', label: 'Tardanza' },
    Ausencia: { bg: '#ffebee', color: '#c62828', label: 'Ausente' },
    Incompleto: { bg: '#e3f2fd', color: '#1565c0', label: 'Incompleto' },
    Licencia: { bg: '#e0f2f1', color: '#00695c', label: 'Licencia' },
    'Art.42': { bg: '#f3e5f5', color: '#6a1b9a', label: 'Art. 42' },
    Suspendido: { bg: '#fbe9e7', color: '#d84315', label: 'Suspendido' },
}[estado] || { bg: '#f5f5f5', color: '#666', label: estado || 'Sin datos' });

export default function MiEquipo() {
    const [tab, setTab] = useState('equipo');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Calendario
    const hoy = new Date();
    const [mes, setMes] = useState(hoy.getMonth() + 1);
    const [ano, setAno] = useState(hoy.getFullYear());
    const [cal, setCal] = useState({ licencias: [], feriados: [] });
    const [calLoading, setCalLoading] = useState(false);

    const cargarEquipo = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf('personal/mi/equipo');
            if (r.ok) { setData(r); setError(''); }
            else { setError(r.mensaje || 'Error'); setData(null); }
        } catch (e) { setError(e.message); }
        setLoading(false);
    }, []);

    const cargarCalendario = useCallback(async () => {
        setCalLoading(true);
        try {
            const r = await jf(`personal/mi/equipo/calendario?mes=${mes}&ano=${ano}`);
            if (r.ok) setCal({ licencias: r.licencias || [], feriados: r.feriados || [] });
        } catch { }
        setCalLoading(false);
    }, [mes, ano]);

    useEffect(() => { cargarEquipo(); }, [cargarEquipo]);
    useEffect(() => { if (tab === 'calendario') cargarCalendario(); }, [tab, cargarCalendario]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>;
    if (error) return <Alert severity="info" sx={{ borderRadius: BR }}>{error}</Alert>;
    if (!data) return null;

    const equipo = data.equipo || [];

    // Cálculos para cabecera
    const presentes = equipo.filter(e => e.EstadoHoy === 'Normal' && !(e.MinTardanza > 0)).length;
    const tardanzas = equipo.filter(e => e.MinTardanza > 0).length;
    const ausencias = equipo.filter(e => e.EstadoHoy === 'Ausencia').length;
    const conLicencia = equipo.filter(e => !!e.LicenciaHoy).length;

    const cambiarMes = (d) => {
        let m = mes + d, a = ano;
        if (m < 1) { m = 12; a--; }
        if (m > 12) { m = 1; a++; }
        setMes(m); setAno(a);
    };

    // Cálculo cobertura: por día, cuántos del equipo están con licencia/ausencia
    const totalDias = new Date(ano, mes, 0).getDate();
    const primerDow = new Date(ano, mes - 1, 1).getDay();
    const feriadosMap = new Map(cal.feriados.map(f => [f.Fecha, f.Motivo]));
    const licenciasPorDia = {};
    for (const l of cal.licencias) {
        const s = new Date(l.Fecha_Desde + 'T12:00:00');
        const e = new Date(l.Fecha_Hasta + 'T12:00:00');
        for (let d = 1; d <= totalDias; d++) {
            const iso = `${ano}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const dt = new Date(ano, mes - 1, d);
            if (dt >= s && dt <= e) {
                if (!licenciasPorDia[iso]) licenciasPorDia[iso] = [];
                licenciasPorDia[iso].push(l);
            }
        }
    }

    // Alerta cobertura: días con 2+ personas ausentes
    const diasRiesgo = Object.entries(licenciasPorDia).filter(([_, arr]) => arr.length >= 2).length;

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 0.5 }}>
                Mi Equipo — {data.area}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
                {data.miRol || 'Jefe'} del área · {equipo.length} personas a tu cargo
            </Typography>

            {/* Cards resumen */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                {[
                    { label: 'Presentes hoy', value: presentes, color: '#2e7d32' },
                    { label: 'Tardanzas hoy', value: tardanzas, color: '#e65100' },
                    { label: 'Ausentes hoy', value: ausencias, color: '#c62828' },
                    { label: 'Con licencia', value: conLicencia, color: '#00695c' },
                ].map(c => (
                    <Paper key={c.label} sx={{ flex: 1, p: 1.5, borderRadius: BR, border: `1px solid ${c.color}22`, textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: c.color }}>{c.value}</Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>{c.label}</Typography>
                    </Paper>
                ))}
            </Box>

            {/* Tabs equipo / calendario */}
            <Paper sx={{ borderRadius: BR, mb: 2, overflow: 'hidden' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                    sx={{ borderBottom: '1px solid #e0e0e0', '& .MuiTab-root': { textTransform: 'none', fontSize: '0.85rem' },
                        '& .Mui-selected': { color: PRIMARY }, '& .MuiTabs-indicator': { bgcolor: PRIMARY } }}>
                    <Tab icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Lista del equipo" value="equipo" />
                    <Tab icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Calendario del equipo" value="calendario" />
                </Tabs>
            </Paper>

            {/* Tab: Lista del equipo */}
            {tab === 'equipo' && (
                <TableContainer component={Paper} sx={{ borderRadius: BR, border: '1px solid #e0e0e0' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                <TableCell sx={{ width: 40 }}></TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Persona</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Función</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Estado hoy</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Detalle</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {equipo.length === 0 ? (
                                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: '#999', py: 3 }}>Sin personal asociado al área</TableCell></TableRow>
                            ) : equipo.map(e => {
                                // Determinar estado visible
                                let estadoFinal = e.EstadoHoy;
                                if (e.LicenciaHoy) estadoFinal = e.LicenciaHoy === 'ART42' ? 'Art.42' : 'Licencia';
                                else if (e.MinTardanza > 0) estadoFinal = 'Tardanza';
                                const est = colorEstadoHoy(estadoFinal);
                                return (
                                    <TableRow key={e.Id} hover>
                                        <TableCell sx={{ py: 0.3 }}>
                                            <Avatar src={`${nodeApiPath(`personal/${e.Id}/foto`)}?token=${localStorage.getItem('SIGIF_JWT_TOKEN')}`}
                                                sx={{ width: 32, height: 32, bgcolor: PRIMARY, fontSize: '0.8rem' }}>
                                                {(e.Apellido || '?')[0]}
                                            </Avatar>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{e.Apellido}, {e.Nombre}</TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem', color: '#666' }}>{e.Funcion || '—'}</TableCell>
                                        <TableCell>
                                            <Chip label={est.label} size="small" sx={{ bgcolor: est.bg, color: est.color, fontWeight: 600, fontSize: '0.7rem', borderRadius: BR }} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem', color: '#666' }}>
                                            {e.MinTardanza > 0 ? `${e.MinTardanza} min tarde` : e.LicenciaHoy ? `Tipo: ${e.LicenciaHoy}` : '—'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Tab: Calendario del equipo */}
            {tab === 'calendario' && (
                <Paper sx={{ p: 2, borderRadius: BR }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <IconButton onClick={() => cambiarMes(-1)} size="small"><ChevronLeftIcon /></IconButton>
                        <Typography variant="h6" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, color: PRIMARY }}>
                            {MESES[mes]} {ano}
                        </Typography>
                        <IconButton onClick={() => cambiarMes(1)} size="small"><ChevronRightIcon /></IconButton>
                    </Box>

                    {diasRiesgo > 0 && (
                        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: BR }}>
                            <strong>Alerta de cobertura:</strong> {diasRiesgo} día{diasRiesgo !== 1 ? 's' : ''} del mes con 2 o más personas del equipo ausentes simultáneamente.
                        </Alert>
                    )}

                    {calLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
                    ) : (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                                {DIAS_SEMANA.map(d => (
                                    <Box key={d} sx={{ textAlign: 'center', py: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: d === 'Dom' || d === 'Sáb' ? '#999' : '#333' }}>{d}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                                {Array.from({ length: primerDow }).map((_, i) => <Box key={`e${i}`} />)}
                                {Array.from({ length: totalDias }).map((_, i) => {
                                    const dia = i + 1;
                                    const iso = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                                    const dow = new Date(ano, mes - 1, dia).getDay();
                                    const esFinde = dow === 0 || dow === 6;
                                    const esFeriado = feriadosMap.has(iso);
                                    const licencias = licenciasPorDia[iso] || [];
                                    const cnt = licencias.length;
                                    const alerta = cnt >= 2;

                                    const bg = esFinde ? '#f5f5f5'
                                        : esFeriado ? '#f3e5f5'
                                        : alerta ? '#ffebee'
                                        : cnt > 0 ? '#fff3e0'
                                        : '#fff';

                                    const tooltip = esFinde ? (dow === 0 ? 'Domingo' : 'Sábado')
                                        : esFeriado ? `Feriado: ${feriadosMap.get(iso)}`
                                        : cnt > 0 ? licencias.map(l => `${l.Apellido}, ${l.Nombre} (${l.Tipo})`).join('\n')
                                        : 'Equipo completo';

                                    return (
                                        <Tooltip key={dia} title={tooltip} arrow placement="top">
                                            <Paper sx={{
                                                p: 0.75, borderRadius: BR, minHeight: 75, bgcolor: bg,
                                                border: alerta ? '2px solid #c62828' : '1px solid #e0e0e0',
                                                opacity: esFinde ? 0.5 : 1,
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: esFinde ? '#999' : '#333' }}>{dia}</Typography>
                                                    {alerta && <WarningAmberIcon sx={{ fontSize: 14, color: '#c62828' }} />}
                                                </Box>
                                                {licencias.slice(0, 3).map((l, idx) => {
                                                    const c = l.Tipo === 'ART42' ? '#6a1b9a' : l.Tipo === 'VACACIONES' ? '#00695c' : l.Tipo === 'CARTA_MEDICA' ? '#bf360c' : '#1565c0';
                                                    return (
                                                        <Typography key={`${l.Id}-${idx}`} variant="caption" sx={{
                                                            fontSize: '0.6rem', display: 'block', color: c, fontWeight: 600,
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {l.Apellido}
                                                        </Typography>
                                                    );
                                                })}
                                                {cnt > 3 && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>+{cnt - 3} más</Typography>}
                                            </Paper>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Paper>
            )}
        </Box>
    );
}
